import { Vehicle } from '@esportal/portal-service';
import { checkOldCar, normalizeToTimestamp } from './common-helpers';
import { COLOR_TOKEN } from './color-tokens';

export function getMonthsBetweenDates(startDate: number | string, endDate: number | string): number {
	const start = new Date(normalizeToTimestamp(startDate));
	const end = new Date(normalizeToTimestamp(endDate));

	let months = (end.getFullYear() - start.getFullYear()) * 12;
	months += end.getMonth() - start.getMonth();

	if (end.getDate() < start.getDate()) {
		months--;
	}

	return Math.max(0, months);
}

export function hasContract(dateInput?: number | string): boolean {
	if (!dateInput) return false;
	const timestamp = normalizeToTimestamp(dateInput);
	return timestamp - Date.now() >= 0;
}

export function getServiceStatusColor(dateInput: number | string, diffDaysCheck?: number): string {
	const today = Date.now();
	const timestamp = normalizeToTimestamp(dateInput);
	const diffInDays = (timestamp - today) / (1000 * 60 * 60 * 24);

	let colorToUse = `${COLOR_TOKEN.signal.success}`;
	if (diffInDays < 0) {
		colorToUse = `${COLOR_TOKEN.signal.error}`;
	} else if (diffDaysCheck && diffInDays <= diffDaysCheck) {
		colorToUse = `${COLOR_TOKEN.signal.progress}`;
	}
	return colorToUse;
}

export function formatUnixToSpanishDate(dateInput: number | string): string {
	const months = [
		'enero',
		'febrero',
		'marzo',
		'abril',
		'mayo',
		'junio',
		'julio',
		'agosto',
		'septiembre',
		'octubre',
		'noviembre',
		'diciembre',
	];

	const timestamp = normalizeToTimestamp(dateInput);
	const date = new Date(timestamp);
	const day = date.getDate();
	const month = months[date.getMonth()];
	const year = date.getFullYear();

	return `${day} de ${month} de ${year}`;
}

export function IDCareExpireDate(dateTimeStamp: number) {
	const registrationDate = new Date(dateTimeStamp);
	const expireDate = new Date(registrationDate);
	expireDate.setFullYear(expireDate.getFullYear() + 8);

	return expireDate.getTime();
}

export function isLongdriveActive(vehicle: Vehicle): boolean {
	return checkOldCar(vehicle.vgedRegistrationDate, 14);
}

export function longdriveProduct(vehicle: Vehicle): string {
	if (!vehicle?.longdrive) return '';

	if (vehicle?.longdrive.isPro) {
		return 'Pro';
	} else {
		if (vehicle?.longdrive.contractType == 'MT LD') return 'Advance';
		else if (vehicle?.longdrive.contractType == 'MT LM') return 'Standard';
	}
	return '';
}

function hasValidWarrantyInfo(vehicle: Vehicle): boolean {
	return !!vehicle.vgedRegistrationDate && vehicle.warranty?.vgedYearsWarrantyCoverage !== undefined;
}

export function warrantyDatePlusYears(registrationDate: string | null, years?: number | null): number | null {
	if (!registrationDate || !years) return null;

	const endDate = new Date(normalizeToTimestamp(registrationDate));
	endDate.setFullYear(endDate.getFullYear() + years);

	return endDate.getTime();
}

export function manufacturerWarrantyEndDate(
	registrationDate: string | null,
	yearsWarrantyCoverage?: number | null,
): number | null {
	return warrantyDatePlusYears(registrationDate, yearsWarrantyCoverage);
}

export function extendedWarrantyEndDate(
	registrationDate: string | null,
	yearsWarrantyCoverage?: number | null,
	yearsWarrantyExtension?: number | null,
): number | null {
	if (!yearsWarrantyCoverage || !yearsWarrantyExtension) return null;

	return warrantyDatePlusYears(registrationDate, yearsWarrantyCoverage + yearsWarrantyExtension);
}

export function isManufacturerWarrantyActive(registrationDate: string | null, yearsWarrantyCoverage?: number): boolean {
	if (!registrationDate || !yearsWarrantyCoverage) return false;

	const manufacturerEndDate = manufacturerWarrantyEndDate(registrationDate, yearsWarrantyCoverage);
	return !!manufacturerEndDate && Date.now() <= manufacturerEndDate;
}

export function isExtendedWarrantyActive(
	registrationDate: string | null,
	yearsWarrantyCoverage?: number,
	yearsWarrantyExtension?: number,
): boolean {
	if (!registrationDate || !yearsWarrantyCoverage || !yearsWarrantyExtension) return false;

	const manufacturerEndDate = manufacturerWarrantyEndDate(registrationDate, yearsWarrantyCoverage);
	const warrantyEndDate = extendedWarrantyEndDate(registrationDate, yearsWarrantyCoverage, yearsWarrantyExtension);

	return (
		!!manufacturerEndDate && !!warrantyEndDate && Date.now() > manufacturerEndDate && Date.now() <= warrantyEndDate
	);
}

export function isWarrantyActive(vehicle: Vehicle): boolean {
	if (!hasValidWarrantyInfo(vehicle) || vehicle.assets.some((asset) => asset.vgedIsVo)) {
		return false;
	}

	return (
		isManufacturerWarrantyActive(vehicle.vgedRegistrationDate, vehicle.warranty?.vgedYearsWarrantyCoverage) ||
		isExtendedWarrantyActive(
			vehicle.vgedRegistrationDate,
			vehicle.warranty?.vgedYearsWarrantyCoverage,
			vehicle.warranty?.vgedYearsWarrantyExtension,
		)
	);
}

export function manufacturerWarrantyTotalMonths(vehicle: Vehicle): number {
	if (!vehicle.warranty?.vgedYearsWarrantyCoverage) return 0;
	return vehicle.warranty.vgedYearsWarrantyCoverage * 12;
}

export function manufacturerWarrantyRemainingMonths(vehicle: Vehicle): number {
	if (!vehicle.vgedRegistrationDate || !vehicle.warranty?.vgedYearsWarrantyCoverage) return 0;

	const registrationPlateTimestamp = new Date(vehicle.vgedRegistrationDate).getTime();
	const totalMonths = vehicle.warranty.vgedYearsWarrantyCoverage * 12;

	const usedMonths = getMonthsBetweenDates(registrationPlateTimestamp, Date.now());

	return Math.max(0, totalMonths - usedMonths);
}

export function hiredWarrantyRemainingMonths(vehicle: Vehicle): number {
	if (
		!vehicle.vgedRegistrationDate ||
		!vehicle.warranty?.vgedYearsWarrantyCoverage ||
		!vehicle.warranty?.vgedYearsWarrantyExtension
	) {
		return 0;
	}

	const now = Date.now();
	const endDate = extendedWarrantyEndDate(
		vehicle.vgedRegistrationDate,
		vehicle.warranty.vgedYearsWarrantyCoverage,
		vehicle.warranty.vgedYearsWarrantyExtension,
	);

	return endDate ? Math.max(0, getMonthsBetweenDates(now, endDate)) : 0;
}

export function hiredWarrantyTotalMonths(vehicle: Vehicle): number {
	if (
		!vehicle.vgedRegistrationDate ||
		!vehicle.warranty?.vgedYearsWarrantyCoverage ||
		!vehicle.warranty?.vgedYearsWarrantyExtension
	) {
		return 0;
	}

	const manufacturerEnd = manufacturerWarrantyEndDate(
		vehicle.vgedRegistrationDate,
		vehicle.warranty.vgedYearsWarrantyCoverage,
	);
	const extensionEnd = extendedWarrantyEndDate(
		vehicle.vgedRegistrationDate,
		vehicle.warranty.vgedYearsWarrantyCoverage,
		vehicle.warranty.vgedYearsWarrantyExtension,
	);

	return manufacturerEnd && extensionEnd ? getMonthsBetweenDates(manufacturerEnd, extensionEnd) : 0;
}

export function isIDcareActive(vehicle: Vehicle): boolean {
	return (
		vehicle.vgedModelGroup.includes('ID.') &&
		checkOldCar(vehicle.vgedRegistrationDate, 8) &&
		vehicle.vgedMileage < 160000
	);
}
